import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { SafetyOutlined, LoadingOutlined } from '@ant-design/icons';
import { ROUTES } from '@utils/constants';
import './styles/processing.css';

interface PaymentResponse {
  id: string;
  paymentUrl: string;
  paymentFormData: {
    key: string;
    txnid: string;
    amount: string;
    productinfo: string;
    firstname: string;
    email: string;
    phone: string;
    surl: string;
    furl: string;
    hash: string;
  };
  htmlForm: string;
}

interface ProcessingState {
  studentId: string;
  student: any;
  selectedPlan: any;
  distance?: number;
  paymentResponse: PaymentResponse;
}

export function Processing() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as ProcessingState;
  const formRef = useRef<HTMLDivElement>(null);
  const [debugInfo, setDebugInfo] = useState<string>('');

  useEffect(() => {
    // Check if we have required state and payment response
    if (!state) {
      console.error('[Processing] No state provided, redirecting to welcome');
      navigate(ROUTES.WELCOME);
      return;
    }

    if (!state.paymentResponse) {
      console.error('[Processing] No payment response in state, redirecting');
      navigate(ROUTES.WELCOME);
      return;
    }

    if (!state.paymentResponse.htmlForm) {
      console.error('[Processing] No HTML form in payment response, redirecting');
      navigate(ROUTES.WELCOME);
      return;
    }

    // Inject and submit the PayU form
    if (formRef.current) {
      try {
        formRef.current.innerHTML = state.paymentResponse.htmlForm;
        
        const form = formRef.current.querySelector('form');
        
        if (form) {
          // Auto-submit after 1 second (matching o-vite-signup-app)
          setTimeout(() => {
            form.submit();
          }, 1000);
        } else {
          const errorMsg = 'ERROR: Form not found in HTML';
          console.error('[Processing]', errorMsg);
          setDebugInfo(errorMsg);
        }
      } catch (error) {
        const errorMsg = `ERROR: ${error}`;
        console.error('[Processing]', errorMsg);
        setDebugInfo(errorMsg);
      }
    } else {
      const errorMsg = 'ERROR: Form ref is null';
      console.error('[Processing]', errorMsg);
      setDebugInfo(errorMsg);
    }
  }, [state, navigate]);

  // If no payment response, show nothing (will redirect)
  if (!state?.paymentResponse) {
    return null;
  }

  const { paymentResponse, selectedPlan } = state;

  return (
    <div className="processing-page">
      {/* Header */}
      <div className="processing-page__header">
        <div className="processing-page__header-content">
          <div className="processing-page__header-icon">
            <LoadingOutlined className="processing-page__spinner" />
          </div>
          <div className="processing-page__header-info">
            <h1 className="processing-page__header-title">Processing Payment</h1>
            <p className="processing-page__header-subtitle">
              Transaction ID: {paymentResponse.id}
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="processing-page__container">
        <div className="processing-page__content">
          {/* Loading Animation */}
          <div className="processing-page__animation">
            <div className="processing-page__pulse"></div>
            <div className="processing-page__icon-wrapper">
              <LoadingOutlined style={{ fontSize: 48 }} className="processing-page__spinner-large" />
            </div>
          </div>

          {/* Status Text */}
          <h2 className="processing-page__title">Redirecting to Payment Gateway</h2>
          <p className="processing-page__subtitle">
            Please wait while we securely redirect you to complete your payment...
          </p>

          {/* Progress Dots */}
          <div className="processing-page__dots">
            <span className="processing-page__dot"></span>
            <span className="processing-page__dot"></span>
            <span className="processing-page__dot"></span>
          </div>

          {/* Payment Details Card */}
          <div className="processing-page__card">
            <div className="processing-page__secure-badge">
              <SafetyOutlined style={{ fontSize: 16 }} />  {/* secure badge */}
              <span>Secure Payment</span>
            </div>

            <div className="processing-page__details">
              <div className="processing-page__detail-row">
                <span className="processing-page__detail-label">Amount</span>
                <span className="processing-page__detail-value processing-page__detail-value--amount">
                  ₹{paymentResponse.paymentFormData?.amount || selectedPlan?.tokenAmount}
                </span>
              </div>
              <div className="processing-page__detail-row">
                <span className="processing-page__detail-label">Transaction ID</span>
                <span className="processing-page__detail-value processing-page__detail-value--mono">
                  {paymentResponse.id}
                </span>
              </div>
              <div className="processing-page__detail-row">
                <span className="processing-page__detail-label">Gateway</span>
                <div className="processing-page__gateway">
                  <div className="processing-page__gateway-icon">P</div>
                  <span className="processing-page__gateway-name">PayU</span>
                </div>
              </div>
            </div>
          </div>

          {/* Security Notice */}
          <div className="processing-page__security">
            <SafetyOutlined style={{ fontSize: 16 }} className="processing-page__security-icon" />
            <span>Your payment is protected by 256-bit SSL encryption</span>
          </div>

          {/* Debug Info */}
          {debugInfo && (
            <div className="processing-page__debug">
              <p>{debugInfo}</p>
            </div>
          )}
        </div>
      </div>

      {/* Hidden form container */}
      <div ref={formRef} style={{ display: 'none' }} />
    </div>
  );
}
